import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ScorecardNavigation from './ScorecardNavigation';
import { ArrowLeft, MapPin, Calendar, Briefcase, Star, TrendingUp, Award, Users, Target, MessageCircle } from 'lucide-react';

// ─── Types ───────────────────────────────────────────────────────────────────

interface WorkHistoryEntry {
  restaurant: string;
  category: string;
  city: string;
  state: string;
  period: string;
  score: number;
  upsellRate: number;
  successRate: number;
  interactions: number;
  greeting: number;
}

interface ProfileData {
  id: string;
  firstName: string;
  lastName: string;
  restaurant: string;
  city: string;
  state: string;
  category: string;
  score: number;
  upsellRate: number;
  successRate: number;
  interactions: number;
  greeting: number;
  rankChange: number;
  bio: string;
  memberSince: string;
  workHistory: WorkHistoryEntry[];
  aspirations?: string;
  certifications?: string[];
}

// ─── Avatar helper ───────────────────────────────────────────────────────────

type Gender = 'female' | 'male';

const SERVER_GENDER: Record<string, Gender> = {
  'sc01': 'female', // Sarah Chen
  'jl01': 'female', // Jamie Liu
  'mp01': 'male',   // Miguel Perez
  'mw01': 'male',   // Marcus Williams
  'ap01': 'male',   // Alex Park
  'ro01': 'female', // Rachel Ortiz
  'em01': 'female', // Emma Martinez
  'dj01': 'male',   // David Johnson
  'tk01': 'male',   // Tyler Kim
  'jb01': 'male',   // Jake Bennett
  'aw01': 'female', // Aisha Washington
  'ls01': 'female', // Lisa Santos
  'kb01': 'female', // Kayla Brown
  'rn01': 'male',   // Ryan Nguyen
  'cm01': 'male',   // Chris Miller
  'ng01': 'female', // Nina Garcia
  'jt01': 'male',   // Jordan Taylor
  'sr01': 'female', // Sofia Rodriguez
  'at01': 'male',   // Andre Thompson
  'hw01': 'female', // Hannah Wilson
  'ms01': 'female', // Mei Ling Sim
  'wt01': 'male',   // Wei Lin Tan
  'ah01': 'male',   // Ahmad Hassan
  'ps01': 'female', // Priya Sharma
  'lk01': 'male',   // Li Wei Koh
  'sy01': 'female', // Siti Yusof
  'rk01': 'male',   // Raj Kumar
  'ct01': 'male',   // Chen Ting
  'dw01': 'male',   // Daniel Wong
  'na01': 'female', // Nurul Aisyah
};

const FEMALE_HAIR = 'bob,bun,straight01,straight02,longButNotTooLong,curvy';
const MALE_HAIR = 'shortFlat,shortRound,shortWaved,sides,theCaesar,theCaesarAndSidePart,shortCurly';
const PROFESSIONAL_CLOTHING = 'blazerAndShirt,blazerAndSweater,collarAndSweater,shirtCrewNeck,shirtScoopNeck';
const PROFESSIONAL_MOUTH = 'default,smile,twinkle,serious';

const getAvatarUrl = (id: string, firstName: string, lastName: string, size = 128) => {
  const gender = SERVER_GENDER[id] || 'male';
  const top = gender === 'female' ? FEMALE_HAIR : MALE_HAIR;
  const facialHairProb = gender === 'female' ? 0 : 25;
  const seed = `${firstName}${lastName}`;
  return `https://api.dicebear.com/9.x/avataaars/svg?seed=${seed}&size=${size}&backgroundColor=b6e3f4,c0aede,d1d4f9,ffd5dc,ffdfbf&top=${top}&facialHairProbability=${facialHairProb}&clothing=${PROFESSIONAL_CLOTHING}&mouth=${PROFESSIONAL_MOUTH}&accessoriesProbability=10`;
};

// ─── Mock Data ───────────────────────────────────────────────────────────────

const MOCK_PROFILES: Record<string, ProfileData> = {
  'sc01': {
    id: 'sc01', firstName: 'Sarah', lastName: 'Chen', restaurant: 'SAJJ Mediterranean', city: 'San Francisco', state: 'CA', category: 'Fast Casual',
    score: 831, upsellRate: 87, successRate: 42, interactions: 1243, greeting: 96, rankChange: 2,
    bio: "Passionate hospitality professional with 5 years of experience across fast casual and casual dining. Known for creating memorable guest experiences through personalized food recommendations and genuine warmth. Believes great service starts with truly listening to what each guest is looking for.",
    aspirations: "My dream is to open my own fast-casual concept that blends Asian and Mediterranean flavors — a nod to my heritage and my love of bold food.",
    memberSince: 'Jan 2025',
    certifications: ['ServSafe Certified', 'Wine & Spirits Level 1'],
    workHistory: [
      { restaurant: 'SAJJ Mediterranean', category: 'Fast Casual', city: 'San Francisco', state: 'CA', period: 'Jan 2025 – Present', score: 831, upsellRate: 87, successRate: 42, interactions: 487, greeting: 96 },
      { restaurant: "Canter's Deli", category: 'Casual Dining', city: 'Los Angeles', state: 'CA', period: 'Mar 2025 – Dec 2025', score: 788, upsellRate: 82, successRate: 38, interactions: 756, greeting: 93 },
    ]
  },
  'em01': {
    id: 'em01', firstName: 'Emma', lastName: 'Martinez', restaurant: 'Le Bernardin', city: 'New York', state: 'NY', category: 'Fine Dining',
    score: 842, upsellRate: 93, successRate: 45, interactions: 654, greeting: 99, rankChange: 1,
    bio: "Fine dining specialist with a deep passion for wine pairing and curating unforgettable tasting experiences. Graduate of the Culinary Institute of America's hospitality management program. Fluent in English, Spanish, and French — which comes in handy at Le Bernardin.",
    aspirations: "My goal is to become the maître d' at a three-Michelin-starred restaurant. Someday, I'd love to bring that level of service to my hometown in New Mexico.",
    memberSince: 'Feb 2025',
    certifications: ['Certified Sommelier (CMS)', 'CIA Hospitality Management', 'ServSafe Manager'],
    workHistory: [
      { restaurant: 'Le Bernardin', category: 'Fine Dining', city: 'New York', state: 'NY', period: 'Jun 2025 – Present', score: 842, upsellRate: 93, successRate: 45, interactions: 432, greeting: 99 },
      { restaurant: 'Eleven Madison Park', category: 'Fine Dining', city: 'New York', state: 'NY', period: 'Feb 2025 – May 2025', score: 819, upsellRate: 88, successRate: 41, interactions: 222, greeting: 97 },
    ]
  },
  'mw01': {
    id: 'mw01', firstName: 'Marcus', lastName: 'Williams', restaurant: 'Gen Korean BBQ', city: 'Los Angeles', state: 'CA', category: 'Casual Dining',
    score: 818, upsellRate: 84, successRate: 40, interactions: 1087, greeting: 95, rankChange: 1,
    bio: "Born and raised in LA, I've spent the last 6 years working in some of the city's most diverse restaurants. I love the energy of casual dining — you get to build real connections with regulars while keeping things fun and fast. My secret? I genuinely love food and it shows when I recommend something.",
    aspirations: "I want to eventually manage my own restaurant. Right now I'm learning everything I can about operations, team leadership, and what makes a great dining experience from the inside out.",
    memberSince: 'Jan 2025',
    certifications: ['ServSafe Certified', 'TIPS Alcohol Training'],
    workHistory: [
      { restaurant: 'Gen Korean BBQ', category: 'Casual Dining', city: 'Los Angeles', state: 'CA', period: 'Feb 2025 – Present', score: 818, upsellRate: 84, successRate: 40, interactions: 543, greeting: 95 },
      { restaurant: 'Sweetgreen', category: 'Fast Casual', city: 'Los Angeles', state: 'CA', period: 'Jan 2025 – Jan 2025', score: 776, upsellRate: 78, successRate: 35, interactions: 432, greeting: 91 },
    ]
  },
  'jb01': {
    id: 'jb01', firstName: 'Jake', lastName: 'Bennett', restaurant: 'Alinea', city: 'Chicago', state: 'IL', category: 'Fine Dining',
    score: 825, upsellRate: 90, successRate: 43, interactions: 432, greeting: 97, rankChange: 0,
    bio: "Grew up in the Chicago restaurant scene — my parents owned a diner on the South Side. That's where I learned that hospitality isn't something you turn on and off, it's who you are. Working at Alinea has pushed me to think about service as theater and every meal as a story.",
    aspirations: "I'm working toward becoming a certified advanced sommelier. Long-term, I'd love to consult for restaurants on building world-class service programs.",
    memberSince: 'Mar 2025',
    certifications: ['Certified Sommelier (CMS)', 'Advanced Cicerone', 'ServSafe Manager'],
    workHistory: [
      { restaurant: 'Alinea', category: 'Fine Dining', city: 'Chicago', state: 'IL', period: 'Mar 2025 – Present', score: 825, upsellRate: 90, successRate: 43, interactions: 432, greeting: 97 },
    ]
  },
  'kb01': {
    id: 'kb01', firstName: 'Kayla', lastName: 'Brown', restaurant: 'Chick-fil-A', city: 'Austin', state: 'TX', category: 'Quick Service',
    score: 798, upsellRate: 82, successRate: 38, interactions: 2567, greeting: 95, rankChange: 4,
    bio: "They say quick service doesn't require great hospitality skills. I disagree. Every guest deserves to feel seen, even in a 30-second interaction. I've consistently been in the top 1% of team members at my location, and I love proving that speed and warmth can coexist.",
    aspirations: "I'm studying hospitality management at UT Austin and want to work in hotel F&B management after graduation.",
    memberSince: 'Jan 2025',
    certifications: ['ServSafe Certified'],
    workHistory: [
      { restaurant: 'Chick-fil-A', category: 'Quick Service', city: 'Austin', state: 'TX', period: 'Jan 2025 – Present', score: 798, upsellRate: 82, successRate: 38, interactions: 2567, greeting: 95 },
    ]
  },
  // Singapore profiles
  'ms01': {
    id: 'ms01', firstName: 'Mei Ling', lastName: 'Sim', restaurant: 'Les Amis', city: 'Orchard Road', state: 'SG', category: 'Fine Dining',
    score: 845, upsellRate: 94, successRate: 46, interactions: 498, greeting: 99, rankChange: 1,
    bio: "Singapore's hospitality scene is among the best in the world, and I'm proud to represent it at Les Amis. With a background in French culinary arts from Le Cordon Bleu, I bring a global perspective to every table. I believe the perfect dining experience is a seamless blend of knowledge, intuition, and genuine care.",
    aspirations: "I aspire to become Singapore's youngest restaurant director at a Michelin-starred establishment. I'm also passionate about mentoring the next generation of hospitality professionals in Southeast Asia.",
    memberSince: 'Jan 2025',
    certifications: ['Le Cordon Bleu Diploma', 'Certified Sommelier (CMS)', 'WSQ Advanced Food Hygiene'],
    workHistory: [
      { restaurant: 'Les Amis', category: 'Fine Dining', city: 'Orchard Road', state: 'SG', period: 'Jan 2025 – Present', score: 845, upsellRate: 94, successRate: 46, interactions: 498, greeting: 99 },
    ]
  },
  'wt01': {
    id: 'wt01', firstName: 'Wei Lin', lastName: 'Tan', restaurant: 'Burnt Ends', city: 'Chinatown', state: 'SG', category: 'Fine Dining',
    score: 836, upsellRate: 91, successRate: 44, interactions: 412, greeting: 98, rankChange: 0,
    bio: "Working at Burnt Ends has taught me that the best hospitality feels effortless but is anything but. Every night is a performance, and the open kitchen concept means our energy directly feeds the guest experience. I love the challenge of reading a table and anticipating what they need before they ask.",
    aspirations: "I want to eventually open a modern Southeast Asian concept in London or New York — bringing our hawker culture heritage to the world stage.",
    memberSince: 'Feb 2025',
    certifications: ['WSET Level 3', 'ServSafe International'],
    workHistory: [
      { restaurant: 'Burnt Ends', category: 'Fine Dining', city: 'Chinatown', state: 'SG', period: 'Feb 2025 – Present', score: 836, upsellRate: 91, successRate: 44, interactions: 412, greeting: 98 },
    ]
  },
  'ah01': {
    id: 'ah01', firstName: 'Ahmad', lastName: 'Hassan', restaurant: 'Hawker Chan', city: 'Chinatown', state: 'SG', category: 'Quick Service',
    score: 811, upsellRate: 85, successRate: 41, interactions: 3247, greeting: 94, rankChange: 2,
    bio: "People underestimate what goes into hawker-style service. We serve thousands every day, and I take pride in making each customer feel welcomed with a smile and a recommendation. The Michelin star at Hawker Chan means people come with high expectations — I love exceeding them in a casual setting.",
    aspirations: "My goal is to help modernize the hawker tradition with technology while keeping the soul of Singapore's food culture alive.",
    memberSince: 'Jan 2025',
    certifications: ['WSQ Food Safety Level 1'],
    workHistory: [
      { restaurant: 'Hawker Chan', category: 'Quick Service', city: 'Chinatown', state: 'SG', period: 'Jan 2025 – Present', score: 811, upsellRate: 85, successRate: 41, interactions: 3247, greeting: 94 },
    ]
  },
  'ps01': {
    id: 'ps01', firstName: 'Priya', lastName: 'Sharma', restaurant: 'Jumbo Seafood', city: 'Clarke Quay', state: 'SG', category: 'Casual Dining',
    score: 802, upsellRate: 83, successRate: 39, interactions: 876, greeting: 95, rankChange: 1,
    bio: "At Jumbo Seafood, every table is a chance to share Singapore's incredible food story. Our chilli crab is legendary, and I love guiding first-time visitors through the menu — pairing dishes, suggesting family-style portions, and making sure everyone leaves happy and full.",
    aspirations: "I dream of managing a flagship restaurant that showcases the best of South and Southeast Asian cuisine under one roof.",
    memberSince: 'Mar 2025',
    certifications: ['ServSafe Certified', 'WSQ Tourism & Hospitality'],
    workHistory: [
      { restaurant: 'Jumbo Seafood', category: 'Casual Dining', city: 'Clarke Quay', state: 'SG', period: 'Mar 2025 – Present', score: 802, upsellRate: 83, successRate: 39, interactions: 876, greeting: 95 },
    ]
  },
  'lk01': {
    id: 'lk01', firstName: 'Li Wei', lastName: 'Koh', restaurant: 'Din Tai Fung', city: 'Orchard Road', state: 'SG', category: 'Fast Casual',
    score: 794, upsellRate: 80, successRate: 37, interactions: 1543, greeting: 92, rankChange: -1,
    bio: "Din Tai Fung is all about precision — in the kitchen and on the floor. I've learned to balance speed with genuine warmth, because even in a busy service, guests notice when you take a moment to recommend the truffle dumplings or suggest a seasonal special.",
    memberSince: 'Feb 2025',
    certifications: ['ServSafe Certified'],
    workHistory: [
      { restaurant: 'Din Tai Fung', category: 'Fast Casual', city: 'Orchard Road', state: 'SG', period: 'Feb 2025 – Present', score: 794, upsellRate: 80, successRate: 37, interactions: 1543, greeting: 92 },
    ]
  },
};

// Servers referenced from leaderboard but without detailed profiles
const BASIC_SERVERS: Record<string, Omit<ProfileData, 'bio' | 'memberSince' | 'workHistory' | 'aspirations' | 'certifications'>> = {
  'jl01': { id: 'jl01', firstName: 'Jamie', lastName: 'Liu', restaurant: 'Lazy Bear', city: 'San Francisco', state: 'CA', category: 'Fine Dining', score: 812, upsellRate: 91, successRate: 38, interactions: 876, greeting: 98, rankChange: 0 },
  'mp01': { id: 'mp01', firstName: 'Miguel', lastName: 'Perez', restaurant: 'Tacolicious', city: 'San Francisco', state: 'CA', category: 'Fast Casual', score: 778, upsellRate: 79, successRate: 35, interactions: 934, greeting: 92, rankChange: -1 },
  'ap01': { id: 'ap01', firstName: 'Alex', lastName: 'Park', restaurant: "Canter's Deli", city: 'Los Angeles', state: 'CA', category: 'Casual Dining', score: 795, upsellRate: 81, successRate: 37, interactions: 1156, greeting: 93, rankChange: 3 },
  'ro01': { id: 'ro01', firstName: 'Rachel', lastName: 'Ortiz', restaurant: 'Sweetgreen', city: 'Los Angeles', state: 'CA', category: 'Fast Casual', score: 762, upsellRate: 76, successRate: 33, interactions: 892, greeting: 90, rankChange: -2 },
  'dj01': { id: 'dj01', firstName: 'David', lastName: 'Johnson', restaurant: 'Shake Shack', city: 'New York', state: 'NY', category: 'Quick Service', score: 806, upsellRate: 83, successRate: 39, interactions: 2156, greeting: 94, rankChange: 0 },
  'tk01': { id: 'tk01', firstName: 'Tyler', lastName: 'Kim', restaurant: 'Sweetgreen', city: 'New York', state: 'NY', category: 'Fast Casual', score: 754, upsellRate: 74, successRate: 31, interactions: 1432, greeting: 88, rankChange: -3 },
  'aw01': { id: 'aw01', firstName: 'Aisha', lastName: 'Washington', restaurant: "Portillo's", city: 'Chicago', state: 'IL', category: 'Quick Service', score: 789, upsellRate: 80, successRate: 36, interactions: 1876, greeting: 91, rankChange: 2 },
  'ls01': { id: 'ls01', firstName: 'Lisa', lastName: 'Santos', restaurant: 'Chipotle', city: 'Chicago', state: 'IL', category: 'Fast Casual', score: 741, upsellRate: 72, successRate: 30, interactions: 2234, greeting: 87, rankChange: 1 },
  'rn01': { id: 'rn01', firstName: 'Ryan', lastName: 'Nguyen', restaurant: 'Pappadeaux', city: 'Dallas', state: 'TX', category: 'Casual Dining', score: 773, upsellRate: 78, successRate: 34, interactions: 1098, greeting: 91, rankChange: -1 },
  'cm01': { id: 'cm01', firstName: 'Chris', lastName: 'Miller', restaurant: 'Canlis', city: 'Seattle', state: 'WA', category: 'Fine Dining', score: 808, upsellRate: 88, successRate: 41, interactions: 543, greeting: 96, rankChange: 0 },
  'ng01': { id: 'ng01', firstName: 'Nina', lastName: 'Garcia', restaurant: 'Versailles', city: 'Miami', state: 'FL', category: 'Casual Dining', score: 767, upsellRate: 77, successRate: 33, interactions: 987, greeting: 89, rankChange: -2 },
  'jt01': { id: 'jt01', firstName: 'Jordan', lastName: 'Taylor', restaurant: 'In-N-Out Burger', city: 'Denver', state: 'CO', category: 'Quick Service', score: 783, upsellRate: 79, successRate: 35, interactions: 1654, greeting: 92, rankChange: 1 },
  'sr01': { id: 'sr01', firstName: 'Sofia', lastName: 'Rodriguez', restaurant: 'Husk', city: 'Nashville', state: 'TN', category: 'Casual Dining', score: 791, upsellRate: 81, successRate: 37, interactions: 876, greeting: 93, rankChange: 2 },
  'at01': { id: 'at01', firstName: 'Andre', lastName: 'Thompson', restaurant: 'Legal Sea Foods', city: 'Boston', state: 'MA', category: 'Casual Dining', score: 776, upsellRate: 78, successRate: 34, interactions: 1123, greeting: 91, rankChange: 0 },
  'hw01': { id: 'hw01', firstName: 'Hannah', lastName: 'Wilson', restaurant: 'The Capital Grille', city: 'Portland', state: 'OR', category: 'Fine Dining', score: 815, upsellRate: 89, successRate: 40, interactions: 567, greeting: 97, rankChange: 1 },
  // Singapore basic profiles
  'sy01': { id: 'sy01', firstName: 'Siti', lastName: 'Yusof', restaurant: 'Lau Pa Sat', city: 'Marina Bay', state: 'SG', category: 'Quick Service', score: 786, upsellRate: 79, successRate: 36, interactions: 2876, greeting: 91, rankChange: 3 },
  'rk01': { id: 'rk01', firstName: 'Raj', lastName: 'Kumar', restaurant: 'Violet Oon', city: 'Marina Bay', state: 'SG', category: 'Casual Dining', score: 779, upsellRate: 78, successRate: 35, interactions: 934, greeting: 90, rankChange: 0 },
  'ct01': { id: 'ct01', firstName: 'Chen', lastName: 'Ting', restaurant: 'PS.Cafe', city: 'Holland Village', state: 'SG', category: 'Casual Dining', score: 768, upsellRate: 76, successRate: 33, interactions: 756, greeting: 89, rankChange: -2 },
  'dw01': { id: 'dw01', firstName: 'Daniel', lastName: 'Wong', restaurant: 'Crystal Jade', city: 'Bugis', state: 'SG', category: 'Fast Casual', score: 758, upsellRate: 75, successRate: 32, interactions: 1234, greeting: 88, rankChange: 1 },
  'na01': { id: 'na01', firstName: 'Nurul', lastName: 'Aisyah', restaurant: 'Tanjong Beach Club', city: 'Sentosa', state: 'SG', category: 'Casual Dining', score: 747, upsellRate: 73, successRate: 31, interactions: 654, greeting: 87, rankChange: -1 },
};

const CATEGORY_STYLES: Record<string, string> = {
  'Quick Service': 'bg-orange-100 text-orange-700',
  'Fast Casual': 'bg-blue-100 text-blue-700',
  'Casual Dining': 'bg-emerald-100 text-emerald-700',
  'Fine Dining': 'bg-purple-100 text-purple-700',
};

function getProfile(serverId: string): ProfileData | null {
  if (MOCK_PROFILES[serverId]) return MOCK_PROFILES[serverId];

  const basic = BASIC_SERVERS[serverId];
  if (!basic) return null;

  return {
    ...basic,
    bio: 'Dedicated hospitality professional committed to exceptional guest experiences and continuous improvement.',
    memberSince: 'Jan 2025',
    workHistory: [
      {
        restaurant: basic.restaurant,
        category: basic.category,
        city: basic.city,
        state: basic.state,
        period: 'Jan 2025 – Present',
        score: basic.score,
        upsellRate: basic.upsellRate,
        successRate: basic.successRate,
        interactions: basic.interactions,
        greeting: basic.greeting,
      }
    ]
  };
}

// ─── Helper Components ───────────────────────────────────────────────────────

const ScoreGauge: React.FC<{ score: number; size?: number }> = ({ score, size = 120 }) => {
  const strokeWidth = 8;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const normalized = Math.max(0, Math.min(1, (score - 300) / 550));
  const offset = circumference * (1 - normalized);
  const color = score >= 750 ? '#10b981' : score >= 650 ? '#6366f1' : score >= 550 ? '#f59e0b' : '#ef4444';
  const label = score >= 800 ? 'Exceptional' : score >= 750 ? 'Excellent' : score >= 700 ? 'Very Good' : score >= 650 ? 'Good' : score >= 600 ? 'Fair' : 'Developing';

  return (
    <div className="flex flex-col items-center">
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#f3f4f6" strokeWidth={strokeWidth} />
        <circle
          cx={size / 2} cy={size / 2} r={radius} fill="none"
          stroke={color} strokeWidth={strokeWidth} strokeLinecap="round"
          strokeDasharray={circumference} strokeDashoffset={offset}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: 'stroke-dashoffset 1s ease-out' }}
        />
        <text x={size / 2} y={size / 2 - 8} textAnchor="middle" dominantBaseline="central"
          style={{ fontSize: size * 0.3, fontWeight: 800, fill: '#111827' }}>
          {score}
        </text>
        <text x={size / 2} y={size / 2 + 16} textAnchor="middle" dominantBaseline="central"
          style={{ fontSize: 11, fontWeight: 500, fill: '#9ca3af' }}>
          {label}
        </text>
      </svg>
      <span className="text-xs text-gray-500 mt-1 font-medium">BespokeScore&trade;</span>
    </div>
  );
};

const StatCard: React.FC<{ icon: React.ReactNode; label: string; value: string; subtext?: string }> = ({ icon, label, value, subtext }) => (
  <div className="bg-white rounded-lg border border-gray-200 p-4">
    <div className="flex items-center gap-2 mb-1">
      {icon}
      <span className="text-xs text-gray-500 font-medium">{label}</span>
    </div>
    <p className="text-2xl font-bold text-gray-900">{value}</p>
    {subtext && <p className="text-xs text-gray-400 mt-0.5">{subtext}</p>}
  </div>
);

const WorkHistoryCard: React.FC<{ entry: WorkHistoryEntry; isCurrent: boolean }> = ({ entry, isCurrent }) => {
  const scoreColor = entry.score >= 750 ? 'text-emerald-600' : entry.score >= 650 ? 'text-indigo-600' : entry.score >= 550 ? 'text-amber-600' : 'text-red-600';
  const scoreBg = entry.score >= 750 ? 'bg-emerald-50' : entry.score >= 650 ? 'bg-indigo-50' : entry.score >= 550 ? 'bg-amber-50' : 'bg-red-50';
  const locationDisplay = entry.state === 'SG' ? `${entry.city}, Singapore` : `${entry.city}, ${entry.state}`;

  return (
    <div className={`bg-white rounded-lg border ${isCurrent ? 'border-indigo-200 ring-1 ring-indigo-100' : 'border-gray-200'} p-5`}>
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="flex items-center gap-2">
            <h4 className="text-base font-semibold text-gray-900">{entry.restaurant}</h4>
            {isCurrent && (
              <span className="inline-flex px-2 py-0.5 text-[10px] font-semibold rounded-full bg-indigo-100 text-indigo-700 uppercase tracking-wider">Current</span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1">
            <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${CATEGORY_STYLES[entry.category] || 'bg-gray-100 text-gray-600'}`}>
              {entry.category}
            </span>
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {locationDisplay}
            </span>
            <span className="text-xs text-gray-500 flex items-center gap-1">
              <Calendar className="w-3 h-3" /> {entry.period}
            </span>
          </div>
        </div>
        <div className={`${scoreBg} rounded-lg px-3 py-2 text-center`}>
          <p className={`text-xl font-bold ${scoreColor}`}>{entry.score}</p>
          <p className="text-[10px] text-gray-400 font-medium">Score</p>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-4 pt-3 border-t border-gray-100">
        <div>
          <p className="text-xs text-gray-500">Upsell Rate</p>
          <p className="text-sm font-semibold text-gray-900">{entry.upsellRate}%</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Success Rate</p>
          <p className="text-sm font-semibold text-gray-900">{entry.successRate}%</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Interactions</p>
          <p className="text-sm font-semibold text-gray-900">{entry.interactions.toLocaleString()}</p>
        </div>
        <div>
          <p className="text-xs text-gray-500">Greeting</p>
          <p className="text-sm font-semibold text-gray-900">{entry.greeting}%</p>
        </div>
      </div>
    </div>
  );
};

// ─── Main Component ──────────────────────────────────────────────────────────

const BespokeScoreProfile: React.FC = () => {
  const { serverId } = useParams();
  const navigate = useNavigate();

  const profile = getProfile(serverId || '');

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50">
        <ScorecardNavigation />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <p className="text-gray-500 text-lg">Profile not found.</p>
          <button onClick={() => navigate('/restaurant/bespoke-score')} className="mt-4 text-indigo-600 hover:text-indigo-800 text-sm font-medium">
            &larr; Back to Leaderboard
          </button>
        </div>
      </div>
    );
  }

  const locationDisplay = profile.state === 'SG' ? `${profile.city}, Singapore` : `${profile.city}, ${profile.state}`;

  return (
    <div className="min-h-screen bg-gray-50">
      <ScorecardNavigation />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Back button */}
        <button
          onClick={() => navigate('/restaurant/bespoke-score')}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Leaderboard
        </button>

        {/* ─── Hero Card ──────────────────────────────────────────── */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden mb-6">
          {/* Top accent bar */}
          <div className="h-2 bg-gradient-to-r from-indigo-500 to-purple-600" />

          <div className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row items-center sm:items-start gap-6">
              {/* Avatar */}
              <img
                src={getAvatarUrl(profile.id, profile.firstName, profile.lastName, 128)}
                alt={`${profile.firstName} ${profile.lastName}`}
                className="w-28 h-28 rounded-2xl shadow-lg flex-shrink-0 bg-gray-100"
              />

              {/* Info */}
              <div className="flex-1 text-center sm:text-left">
                <h1 className="text-2xl font-bold text-gray-900">{profile.firstName} {profile.lastName}</h1>
                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3 mt-2">
                  <span className={`inline-flex px-2.5 py-0.5 text-xs font-medium rounded-full ${CATEGORY_STYLES[profile.category] || 'bg-gray-100 text-gray-600'}`}>
                    {profile.category}
                  </span>
                  <span className="text-sm text-gray-500 flex items-center gap-1">
                    <MapPin className="w-3.5 h-3.5" /> {locationDisplay}
                  </span>
                  <span className="text-sm text-gray-500 flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" /> Member since {profile.memberSince}
                  </span>
                </div>
                <p className="text-sm text-gray-500 mt-1 flex items-center justify-center sm:justify-start gap-1">
                  <Briefcase className="w-3.5 h-3.5" /> Currently at {profile.restaurant}
                </p>

                {/* Certifications */}
                {profile.certifications && profile.certifications.length > 0 && (
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mt-3">
                    {profile.certifications.map(cert => (
                      <span key={cert} className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-medium rounded-full bg-gray-100 text-gray-600">
                        <Award className="w-2.5 h-2.5" /> {cert}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Score Gauge */}
              <div className="flex-shrink-0">
                <ScoreGauge score={profile.score} size={130} />
              </div>
            </div>
          </div>
        </div>

        {/* ─── Key Stats ──────────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={<Target className="w-4 h-4 text-blue-500" />}
            label="Upsell Rate"
            value={`${profile.upsellRate}%`}
            subtext="Of interactions"
          />
          <StatCard
            icon={<Star className="w-4 h-4 text-amber-500" />}
            label="Success Rate"
            value={`${profile.successRate}%`}
            subtext="Of upsell attempts"
          />
          <StatCard
            icon={<Users className="w-4 h-4 text-emerald-500" />}
            label="Total Interactions"
            value={profile.interactions.toLocaleString()}
            subtext="Career total"
          />
          <StatCard
            icon={<TrendingUp className="w-4 h-4 text-indigo-500" />}
            label="Greeting Score"
            value={`${profile.greeting}%`}
            subtext="Guest greetings"
          />
        </div>

        {/* ─── Work History ───────────────────────────────────────── */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-gray-400" />
            Work History
          </h2>
          <div className="space-y-4">
            {profile.workHistory.map((entry, idx) => (
              <WorkHistoryCard key={`${entry.restaurant}-${entry.period}`} entry={entry} isCurrent={idx === 0} />
            ))}
          </div>
        </div>

        {/* ─── About ──────────────────────────────────────────────── */}
        <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <MessageCircle className="w-5 h-5 text-gray-400" />
            About
          </h2>
          <p className="text-sm text-gray-600 leading-relaxed">{profile.bio}</p>

          {profile.aspirations && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-500 uppercase tracking-wider font-medium mb-1">Aspirations</p>
              <p className="text-sm text-gray-600 leading-relaxed italic">"{profile.aspirations}"</p>
            </div>
          )}
        </div>

        {/* Score scale */}
        <div className="flex items-center justify-center gap-6 text-xs text-gray-400 mb-8">
          <span>BespokeScore&trade; Scale: 300–850</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-emerald-500" /> 750+ Excellent</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-indigo-500" /> 650–749 Good</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-amber-500" /> 550–649 Fair</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500" /> &lt;550 Developing</span>
        </div>
      </div>
    </div>
  );
};

export default BespokeScoreProfile;
